import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SelectField from '../ui/SelectField';
import { useGetTaxCategoriesQuery } from '../../api/taxCategory/taxCategoryApiSlice';
import { formatRatePercent } from './formatRatePercent';

interface TaxCategorySelectProps {
    /** The assigned category id, or '' when unassigned. */
    value: string;
    onChange: (taxCategoryId: string) => void;
    disabled?: boolean;
    label?: string;
    /** Rendered as an (i) tooltip — the field is genuinely non-obvious. */
    help?: string;
    className?: string;
}

/**
 * The VAT category picker (#1353) — one component, used by the Product form,
 * each add-on service row and anywhere else a supply gets a tax treatment.
 *
 * A category, never a percentage. Rates change (Germany cut 19→16 / 7→5 for
 * H2 2020 and put them back) and the applicable one is fixed by the date of
 * supply, so a number typed once would silently misprice everything after the
 * next change. kolula owns the catalogue; tenants assign from it (ADR-0003
 * tier 2).
 *
 * The rate shown beside each name is the backend's answer for this tenant's
 * country — not computed here. A client resolving rates would be a second
 * implementation of the rule the backend owns, and those are what #1353 spent
 * three PRs removing.
 */
const TaxCategorySelect = ({
    value,
    onChange,
    disabled,
    label,
    help,
    className,
}: Readonly<TaxCategorySelectProps>) => {
    const { t } = useTranslation();
    const { data, isLoading, isError } = useGetTaxCategoriesQuery();

    // A category with no rate row for this country cannot price a booking, so
    // it is not offered. Offering it would let a tenant assign something that
    // makes every booking on that offering unpriceable for VAT — an error
    // surfaced far from the screen that caused it.
    const usable = useMemo(
        () => (data?.categories ?? []).filter((c) => c.currentRatePercent !== null),
        [data],
    );

    const options = useMemo(() => usable.map((c) => c.id), [usable]);

    const optionLabels = useMemo(() => {
        const labels: Record<string, string> = {};
        for (const category of usable) {
            const name = t(`tax_category.code.${category.code}`, {
                defaultValue: category.description || category.code,
            });
            const rate = category.currentRatePercent as number;
            labels[category.id] = `${name} (${formatRatePercent(rate)}%)`;
        }
        return labels;
    }, [usable, t]);

    // A failure must never look like an absence: an empty picker reads as
    // "nothing matches", when the truth is that the catalogue could not be
    // loaded at all — a different problem with a different fix.
    const noOptions = isError
        ? t('tax_category.load_failed')
        : t('tax_category.none_for_country');

    return (
        <SelectField
            labelAbove
            label={label ?? t('tax_category.label')}
            {...(help === undefined ? {} : { help })}
            value={value}
            onChange={onChange}
            options={options}
            optionLabels={optionLabels}
            loading={isLoading}
            noOptions={noOptions}
            placeholder={t('tax_category.placeholder')}
            {...(disabled === undefined ? {} : { disabled })}
            {...(className === undefined ? {} : { className })}
        />
    );
};

export default TaxCategorySelect;
